import { SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { addressService } from 'app/shared/services/address/address.service';
import { featureService } from 'app/shared/services/feature/feature-service';
import { AddressDetailModel } from 'app/shared/models/address/address.model';
import { FeatureKey } from 'app/shared/models/feature-key';
import { ProductCategory } from 'app/shared/models/product-category.model';
import { ProductSessionData, SelectedProductSessionData } from 'app/shared/models/sessionData';
import { ProductsResponse } from 'app/shared/models/product-response.model';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { clearCart } from 'app/bundle/store/actions/cartActions';

class ProductsSessionStorageService {
  private getAllProducts<T extends ProductsResponse = ProductsResponse>(): ProductSessionData<T>[] {
    return JSON.parse(sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
  }

  private saveProducts(products: ProductSessionData[]): void {
    if (products?.length > 0) {
      window.sessionStorage.setItem(SessionStorageKeys.ProductDetailsSession, JSON.stringify(products));
    }
  }

  public saveMobileProducts(products: SelectedProductSessionData): void {
    if (products) {
      window.sessionStorage.setItem(SessionStorageKeys.SelectedMobileProductDetails, JSON.stringify(products));
      store.set(StoreKeys.selectedMobileProductDetails, products);

      // Trigger clearCart when removing the last mobile plan item (which is also the last item in cart)
      const cartDetails = store.get(StoreKeys.cart);
      const removingLastMobileItem = !products?.productDetails || products?.productDetails?.length === 0;
      const isOnlyMobileInCart = !cartDetails?.cartItems?.find(
        (item) => item.additionalData?.productCategory?.toLowerCase() !== 'mobile plan'
      );
      if (removingLastMobileItem && isOnlyMobileInCart) {
        clearCart();
      }
    }
  }

  private buildCacheKey(address?: AddressDetailModel): string {
    return `${address ? addressService.convertAddressToString(address) : 'default'}`;
  }

  private setProducts(response: any, productCategory: ProductCategory, address?: AddressDetailModel | string): void {
    const couponCodeInRequest = store.get('couponCode') ?? null;
    const sessionObject: ProductSessionData = {
      requestType: address ? 'address' : 'default',
      requestCouponCode: couponCodeInRequest,
      key: typeof address === 'string' ? address : this.buildCacheKey(address),
      productCategory,
      response,
      address: typeof address === 'string' ? null : address,
      webCampaignId: store.get(StoreKeys.webId)
    };

    const allProducts = this.getAllProducts();
    let newAllProducts;
    if (!allProducts) {
      newAllProducts = [sessionObject];
    } else if (address) {
      newAllProducts = [sessionObject, ...allProducts];
    } else {
      // Add default products to end of the array
      newAllProducts = [...allProducts, sessionObject];
    }

    this.saveProducts(newAllProducts);
  }

  public upsertProducts(response: any, productCategory: ProductCategory, address?: AddressDetailModel | string): void {
    const products = this.getAllProducts();
    const key = typeof address === 'string' ? address : this.buildCacheKey(address);
    const productIndex = products?.findIndex((p) => p.key === key);

    if (products && productIndex !== -1) {
      // remove element and add to start of array
      const productSessionData = products.splice(productIndex, 1)[0];
      products.unshift(productSessionData);
      this.saveProducts(products);
    } else {
      this.setProducts(response, productCategory, address);
    }
  }

  public getLatestProductsByProductCategory<T extends ProductsResponse>(productCategory: ProductCategory): ProductSessionData<T> {
    return this.getAllProducts<T>()?.find((x) => x.productCategory === productCategory) ?? null;
  }

  public getLatestProducts<T extends ProductsResponse>(
    productCategory: ProductCategory,
    address?: AddressDetailModel
  ): ProductSessionData<T> {
    const key = this.buildCacheKey(address);
    let storageProductSessionData = this.getAllProducts<T>()?.find((x) => x.productCategory === productCategory && x.key === key) ?? null;
    const couponCodeInRequest = store.get('couponCode') ?? null;
    const couponCodeInCache = storageProductSessionData?.requestCouponCode ?? null;
    const isNbnMoversFlow =
      sessionStorage.getItem('appConfig') === 'movers' && featureService.getFeatureStatus(FeatureKey.NBN_MOVERS_ENABLED);
    // When couponCode has changed, clear products cache
    if (couponCodeInRequest !== couponCodeInCache || (isNbnMoversFlow && productCategory !== 'mobile plan')) {
      storageProductSessionData = null;
      sessionStorage.removeItem(SessionStorageKeys.ProductDetailsSession);
    }
    return storageProductSessionData;
  }

  public getNbnProductsByLocationId<T extends ProductsResponse>(locationId: string) {
    return (
      this.getAllProducts<T>()?.find(
        (x) =>
          x.key === locationId ||
          (x.productCategory === 'nbn' && x.response.serviceQualification?.nbn.nbnLocation[0]?.locationId === locationId)
      ) ?? null
    );
  }

  public getLatestWebIdFromProducts(): string {
    const products: ProductSessionData<ProductsResponse>[] = JSON.parse(sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    if (products?.length) {
      return products[0]?.webCampaignId;
    }

    return null;
  }

  public removeProducts(): void {
    sessionStorage.removeItem(SessionStorageKeys.ProductDetailsSession);
  }
}

export const productsSessionStorageService = new ProductsSessionStorageService();
